import { NgModule } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ContextMenu } from 'primeng/contextmenu';
import { DatePicker } from 'primeng/datepicker';
import { Drawer } from 'primeng/drawer';
import { InputMaskDirective } from 'primeng/inputmask';
import { Tree } from 'primeng/tree';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { InputNumber } from 'primeng/inputnumber';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { InputTextModule } from 'primeng/inputtext';
import { Menu } from 'primeng/menu';
import { MultiSelect } from 'primeng/multiselect';
import { Password } from 'primeng/password';
import { Select } from 'primeng/select';
import { SelectModule } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { Textarea } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';
import { PrimeNGModule } from './primeng/primeng.module';
import { SafeResourceUrlPipe } from './pipes/safe-resource-url.pipe';
import { KlDatePipe } from './pipes/kl-date.pipe';
import { KlGridCellValuePipe } from './pipes/kl-grid-cell-value.pipe';
import { KlGridCellSeverityPipe } from './pipes/kl-grid-cell-severity.pipe';
import { KlAlertComponent } from './components/kl-alert/kl-alert';
import { KlColumnFilter } from './components/kl-grid/kl-column-filter/kl-column-filter';
import { KlGridColumnSettingsDrawer } from './components/kl-grid/kl-grid-column-settings-drawer/kl-grid-column-settings-drawer';
import { KlGridContainer } from './components/kl-grid/kl-grid-container/kl-grid-container';
import { KlGridToolbar } from './components/kl-grid/kl-grid-container/kl-grid-toolbar/kl-grid-toolbar';
import { KlGrid } from './components/kl-grid/kl-grid';
import { KlTransaction } from './components/kl-transaction/kl-transaction';
import { KlTransactionGrid } from './components/kl-transaction/kl-transaction-grid/kl-transaction-grid';
import { KlTransactionToolbar } from './components/kl-transaction/kl-transaction-toolbar/kl-transaction-toolbar';
import { KlGridRowDetailDirective } from './components/kl-grid/kl-grid-row-detail.directive';
import { KlGridForm } from './components/kl-grid-form/kl-grid-form';
import { KlGridFormBodyCell } from './components/kl-grid-form/kl-grid-form-body-cell/kl-grid-form-body-cell';
import { KlGridFormToolbar } from './components/kl-grid-form/kl-grid-form-toolbar/kl-grid-form-toolbar';
import { KlFormContainer } from './components/kl-form-container/kl-form-container';
import { KlImageFieldComponent } from './components/kl-image-field/kl-image-field';
import { KlImageCropperDialogComponent } from './components/kl-image-cropper-dialog/kl-image-cropper-dialog';
import { KlInputComponent } from './components/kl-input/kl-input';
import { KlFieldsetComponent } from './components/kl-fieldset/kl-fieldset';
import { KlLabelComponent } from './components/kl-label/kl-label';
import { KlMultiselectComponent } from './components/kl-multiselect/kl-multiselect';
import { KlListboxComponent } from './components/kl-listbox/kl-listbox';
import { KlTreeComponent } from './components/kl-tree/kl-tree';
import { KlParametersComponent } from './components/kl-parameters/kl-parameters';
import { KlReceiptPrint } from './components/kl-receipt-print/kl-receipt-print';
import { KlSelectComponent } from './components/kl-select/kl-select';
import { KlTextareaComponent } from './components/kl-textarea/kl-textarea';
import { KlUploadFileComponent } from './components/kl-upload-file/kl-upload-file';
import { KlDatePickerComponent } from './components/kl-date-picker/kl-date-picker';
import { DkDateTimePickerComponent } from './components/dk-date-time-picker/dk-date-time-picker';
import { KlConfirmDialogComponent } from './components/kl-confirm-dialog/kl-confirm-dialog';
import { KlEntryInfoDialogComponent } from './components/kl-entry-info-dialog/kl-entry-info-dialog';
import { KlRecordHistoryDialogComponent } from './components/kl-record-history-dialog/kl-record-history-dialog';
import { KlCellCopyOnDblclickDirective } from './directives/kl-cell-copy-on-dblclick.directive';
import { KlReport } from './components/kl-report/kl-report';
import { KlLoadingSkeletonComponent } from './components/kl-loading-skeleton/kl-loading-skeleton';
import { KlLoader } from './components/kl-loader/kl-loader';
import { KlDetailFilterShell } from './components/kl-detail-filter-shell/kl-detail-filter-shell';
import { KlDetailFilterComponent } from './components/kl-detail-filter/kl-detail-filter';

/**
 * Shared module: common Angular + PrimeNG shell and all `kl-*` components (non-standalone).
 * Feature standalone components import only `SharedModule`.
 */
@NgModule({
  imports: [
    DragDropModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    PrimeNGModule,
    TableModule,
    ContextMenu,
    Menu,
    Skeleton,
    Tooltip,
    InputText,
    InputNumber,
    Password,
    Textarea,
    Select,
    MultiSelect,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    Drawer,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    DatePicker,
    InputMaskDirective,
    Tree,
    ImageCropperComponent,
    KlReport,
    KlLoadingSkeletonComponent,
    KlLoader,
    KlFieldsetComponent,
    KlLabelComponent,
    KlUploadFileComponent,
    SafeResourceUrlPipe,
    KlCellCopyOnDblclickDirective,
    KlEntryInfoDialogComponent,
    KlRecordHistoryDialogComponent,
    KlGridRowDetailDirective,
  ],
  declarations: [
    KlAlertComponent,
    KlInputComponent,
    KlTextareaComponent,
    KlDatePickerComponent,
    DkDateTimePickerComponent,
    KlConfirmDialogComponent,
    KlSelectComponent,
    KlMultiselectComponent,
    KlListboxComponent,
    KlTreeComponent,
    KlImageFieldComponent,
    KlImageCropperDialogComponent,
    KlFormContainer,
    KlColumnFilter,
    KlGridColumnSettingsDrawer,
    KlGridContainer,
    KlGridToolbar,
    KlGrid,
    KlTransaction,
    KlTransactionGrid,
    KlTransactionToolbar,
    KlGridForm,
    KlGridFormBodyCell,
    KlGridFormToolbar,
    KlParametersComponent,
    KlReceiptPrint,
    KlDatePipe,
    KlGridCellValuePipe,
    KlGridCellSeverityPipe,
    KlDetailFilterShell,
    KlDetailFilterComponent,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    PrimeNGModule,
    TableModule,
    ContextMenu,
    Menu,
    Skeleton,
    Tooltip,
    InputText,
    InputNumber,
    Password,
    Textarea,
    Select,
    MultiSelect,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    Drawer,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    DatePicker,
    InputMaskDirective,
    Tree,
    KlReport,
    KlLoadingSkeletonComponent,
    KlLoader,
    KlFieldsetComponent,
    KlLabelComponent,
    KlUploadFileComponent,
    SafeResourceUrlPipe,
    KlAlertComponent,
    KlInputComponent,
    KlTextareaComponent,
    KlDatePickerComponent,
    DkDateTimePickerComponent,
    KlConfirmDialogComponent,
    KlSelectComponent,
    KlMultiselectComponent,
    KlListboxComponent,
    KlTreeComponent,
    KlImageFieldComponent,
    KlFormContainer,
    KlColumnFilter,
    KlGridColumnSettingsDrawer,
    KlGridContainer,
    KlGrid,
    KlTransaction,
    KlTransactionGrid,
    KlTransactionToolbar,
    KlGridForm,
    KlParametersComponent,
    KlDatePipe,
    KlDetailFilterShell,
    KlDetailFilterComponent,
    KlCellCopyOnDblclickDirective,
    KlGridRowDetailDirective,
  ],
})
export class SharedModule {}
